import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, CircularProgress, Popper, Stack, TextField, Typography} from 'views/components';
import {InputAdornment} from '@mui/material';

import {Translate, translate} from '../Translate';

import backend from 'utils/backend';
import _ from 'lodash';

const EMPTY_ARRAY = [];

const SlashCommandTextbox = (props) => {
  const {
    autoFocus,
    caregivers = EMPTY_ARRAY,
    clients = EMPTY_ARRAY,
    isExecutingCommand,
    onMentionedResourcesChange,
    onExecutePlaybook,
    onExecuteSkill,
    placeholder,
    playbooks,
    onInputValueChange,
    skills,
  } = props;

  const [value, setValue] = React.useState('');
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const [activeMentionCommand, setActiveMentionCommand] = React.useState(null);
  const [availableCaregivers, setAvailableCaregivers] = React.useState(caregivers);
  const [availableClients, setAvailableClients] = React.useState(clients);
  const [isMentionCommandLoading, setIsMentionCommandLoading] = React.useState(false);

  const anchorRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const commandColors = {
    '#': '#958ACC',
    '$': '#958ACC',
  };

  const availableSkills = React.useMemo(() => {
    return [...skills]
        .sort((left, right) => (left?.name || '').localeCompare(right?.name || ''));
  }, [skills]);

  React.useEffect(() => {
    setAvailableCaregivers((previousCaregivers) => _.uniqBy(
        [...caregivers, ...previousCaregivers],
        (caregiver) => caregiver?.caregiverRn || caregiver?.connectionRn,
    ));
  }, [caregivers]);

  React.useEffect(() => {
    setAvailableClients((previousClients) => _.uniqBy(
        [...clients, ...previousClients],
        (client) => client?.clientRn || client?.connectionRn,
    ));
  }, [clients]);

  const availableCommands = React.useMemo(() => {
    const skillCommands = availableSkills.map((skill) => ({
      key: skill.skillRn,
      name: skill.name,
      type: 'skill',
      item: skill,
    }));
    const playbookCommands = playbooks.map((playbook) => ({
      key: playbook.playbookRn,
      name: playbook.title,
      type: 'playbook',
      item: playbook,
    }));

    return [...skillCommands, ...playbookCommands];
  }, [availableSkills, playbooks]);

  const mentionDefinitions = React.useMemo(() => {
    return {
      '#': availableCaregivers.map((caregiver) => {
        const display = caregiver?.caregiverName ||
          caregiver?.name ||
          `${caregiver?.firstName || ''} ${caregiver?.lastName || ''}`.trim();
        const rn = caregiver?.caregiverRn || caregiver?.connectionRn;

        if (!display || !rn) {
          return null;
        }

        return {
          connection: {
            rn,
            type: 'CAREGIVER',
          },
          display,
        };
      }).filter(Boolean),
      '$': availableClients.map((client) => {
        const display = client?.clientName ||
          client?.name ||
          `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
        const rn = client?.clientRn || client?.connectionRn;

        if (!display || !rn) {
          return null;
        }

        return {
          connection: {
            rn,
            type: 'CLIENT',
          },
          display,
        };
      }).filter(Boolean),
    };
  }, [availableCaregivers, availableClients]);

  const resolveActiveCommand = React.useCallback((params) => {
    const {
      value,
      caretPosition,
      triggerSymbols,
    } = params;
    const caret = typeof caretPosition === 'number' ? caretPosition : value.length;
    const beforeCaret = value.slice(0, caret);

    for (let index = beforeCaret.length - 1; index >= 0; index -= 1) {
      const symbol = beforeCaret[index];

      if (!triggerSymbols.includes(symbol)) {
        continue;
      }

      if (index > 0 && !/\s/.test(beforeCaret[index - 1])) {
        continue;
      }

      const rawQuery = beforeCaret.slice(index + 1);

      if (rawQuery.includes('\n')) {
        return null;
      }

      if (/\s/.test(rawQuery) || !/^[a-z0-9-]*$/i.test(rawQuery)) {
        return null;
      }

      return {
        end: caret,
        query: rawQuery.toLowerCase(),
        start: index,
        trigger: symbol,
      };
    }

    return null;
  }, []);

  const applyMentionOption = React.useCallback((definition) => {
    if (!activeMentionCommand || !definition?.display) {
      return;
    }

    const beforeCommand = value.slice(0, activeMentionCommand.start);
    const afterCommand = value.slice(activeMentionCommand.end);
    const insertedToken = `${activeMentionCommand.trigger}${definition.display}`;
    const needsTrailingSpace = afterCommand.length > 0 && !/^\s/.test(afterCommand);
    const nextValue = `${beforeCommand}${insertedToken}${needsTrailingSpace ? ' ' : ''}${afterCommand}`;
    const nextCaret = beforeCommand.length + insertedToken.length + (needsTrailingSpace ? 1 : 0);

    setValue(nextValue);
    onInputValueChange(nextValue);
    setIsMenuOpen(false);
    setActiveMentionCommand(null);

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange?.(nextCaret, nextCaret);
    });
  }, [activeMentionCommand, onInputValueChange, value]);

  const matchMentionAt = React.useCallback((text, startIndex) => {
    const trigger = text[startIndex];
    const definitions = mentionDefinitions[trigger] || [];

    if (!definitions.length) {
      return null;
    }

    if (startIndex > 0 && !/\s/.test(text[startIndex - 1])) {
      return null;
    }

    const trailingText = text.slice(startIndex + 1);
    const trailingLower = trailingText.toLowerCase();
    let bestMatch = null;

    definitions.forEach((definition) => {
      const matchValue = definition.display.toLowerCase();

      if (!trailingLower.startsWith(matchValue)) {
        return;
      }

      const nextChar = trailingText[matchValue.length];
      if (nextChar && !/[\s,.;:!?)]/.test(nextChar)) {
        return;
      }

      if (!bestMatch || matchValue.length > bestMatch.matchLength) {
        bestMatch = {
          definition,
          matchLength: matchValue.length,
          trigger,
        };
      }
    });

    if (!bestMatch) {
      return null;
    }

    return {
      ...bestMatch,
      index: startIndex,
      length: bestMatch.matchLength + 1,
    };
  }, [mentionDefinitions]);

  const extractRecognizedMentions = React.useCallback((text) => {
    const recognized = [];
    let cursor = 0;

    while (cursor < text.length) {
      const mentionMatch = matchMentionAt(text, cursor);

      if (!mentionMatch) {
        cursor += 1;
        continue;
      }

      recognized.push(mentionMatch);
      cursor += mentionMatch.length;
    }

    return recognized;
  }, [matchMentionAt]);

  const renderHighlightedText = React.useCallback((text) => {
    if (!text) {
      return null;
    }

    const mentionRanges = extractRecognizedMentions(text)
        .sort((left, right) => left.index - right.index);
    const segments = [];
    let cursor = 0;

    mentionRanges.forEach((range, index) => {
      if (range.index > cursor) {
        segments.push({
          key: `plain-${index}-${cursor}`,
          text: text.slice(cursor, range.index),
        });
      }

      segments.push({
        color: commandColors[range.trigger],
        key: `mention-${index}-${range.index}`,
        text: text.slice(range.index, range.index + range.length),
      });

      cursor = range.index + range.length;
    });

    if (cursor < text.length) {
      segments.push({
        key: `plain-tail-${cursor}`,
        text: text.slice(cursor),
      });
    }

    return (
      <Box
        sx={{
          px: '16px',
          py: '10px',
          fontSize: '11px',
          lineHeight: '150%',
          whiteSpace: 'pre',
          color: '#1A1916',
          overflow: 'hidden',
        }}
      >
        {
          segments.map((segment) => (
            <Box
              key={segment.key}
              component='span'
              sx={segment.color ? {color: segment.color} : undefined}
            >
              {segment.text}
            </Box>
          ))
        }
      </Box>
    );
  }, [commandColors, extractRecognizedMentions]);

  const command = React.useMemo(() => {
    if (!value.startsWith('/')) {
      return {
        isSlashCommand: false,
        commandName: '',
        isComplete: false,
        promptText: '',
      };
    }

    const matchedCommand = [...availableCommands]
        .filter((commandOption) => {
          const commandPrefix = `/${commandOption.name}`;
          return value === commandPrefix || value.startsWith(`${commandPrefix} `);
        })
        .sort((left, right) => (right?.name || '').length - (left?.name || '').length)[0] || null;

    if (matchedCommand?.name) {
      const matchedCommandPrefix = `/${matchedCommand.name}`;
      const isExactMatch = value === matchedCommandPrefix;
      const hasPromptText = value.length > matchedCommandPrefix.length;

      return {
        isSlashCommand: true,
        commandName: matchedCommand.name,
        isComplete: matchedCommand.type === 'playbook' ? isExactMatch || hasPromptText : hasPromptText,
        promptText: hasPromptText ? value.slice(matchedCommandPrefix.length + 1) : '',
      };
    }

    const firstSpaceIndex = value.indexOf(' ');

    return {
      isSlashCommand: true,
      commandName: value.slice(1, firstSpaceIndex === -1 ? value.length : firstSpaceIndex),
      isComplete: firstSpaceIndex !== -1,
      promptText: firstSpaceIndex === -1 ? '' : value.slice(firstSpaceIndex + 1),
    };
  }, [availableCommands, value]);

  const filteredCommands = command.isSlashCommand && !command.isComplete ?
    availableCommands.filter((commandOption) => {
      if (!command.commandName) {
        return true;
      }

      const escapedCommandName = command.commandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`^${escapedCommandName}`, 'i').test(commandOption?.name || '');
    }) :
    [];

  const selectedCommand = availableCommands.find(
      (commandOption) => (commandOption?.name || '').toLowerCase() === command.commandName.toLowerCase(),
  ) || null;
  const mentionOptions = React.useMemo(() => {
    if (!activeMentionCommand || !selectedCommand) {
      return [];
    }

    const query = activeMentionCommand.query.trim().toLowerCase();
    const definitions = mentionDefinitions[activeMentionCommand.trigger] || [];

    return definitions.filter((definition) => !query || definition.display.toLowerCase().includes(query));
  }, [activeMentionCommand, mentionDefinitions, selectedCommand]);
  const showSlashMenu = isMenuOpen && command.isSlashCommand && !command.isComplete && !activeMentionCommand;
  const showMentionMenu = isMenuOpen && Boolean(activeMentionCommand);
  const showMenu = showSlashMenu || showMentionMenu;
  const showHighlightedOverlay = Boolean(value && selectedCommand);
  const menuWidth = anchorRef.current ? `${anchorRef.current.offsetWidth}px` : '100%';

  const focusInput = () => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleChange = (nextValue) => {
    setValue(nextValue);
    onInputValueChange(nextValue);
  };

  const selectCommand = (commandOption) => {
    if (!commandOption?.name) {
      return;
    }

    setIsMenuOpen(false);
    setValue(`/${commandOption.name} `);
    focusInput();
  };

  const executeCommand = async () => {
    if (isExecutingCommand || !selectedCommand || !command.isComplete) {
      return;
    }

    const mentionedResources = _.uniqBy(
        extractRecognizedMentions(value)
            .map((mention) => mention.definition?.connection)
            .filter(Boolean),
        (resource) => resource.rn,
    );

    const response = selectedCommand.type === 'playbook' ?
      await onExecutePlaybook(selectedCommand.item, mentionedResources) :
      await onExecuteSkill(selectedCommand.item, command.promptText, mentionedResources);

    if (response !== null) {
      setValue('');
      setIsMenuOpen(false);
      setActiveMentionCommand(null);
    }
  };

  const handleKeyDown = (event) => {
    if (showMentionMenu) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        setActiveMentionCommand(null);
        return;
      }

      if (mentionOptions.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % mentionOptions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + mentionOptions.length) % mentionOptions.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const nextMention = mentionOptions[highlightedIndex] || mentionOptions[0];

        if (nextMention) {
          event.preventDefault();
          applyMentionOption(nextMention);
        }
        return;
      }
    }

    if (showSlashMenu) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        return;
      }

      if (filteredCommands.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredCommands.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const nextCommand = filteredCommands[highlightedIndex] || filteredCommands[0];

        if (nextCommand) {
          event.preventDefault();
          selectCommand(nextCommand);
        }
        return;
      }
    }

    if (event.key === 'Enter' && command.isSlashCommand) {
      event.preventDefault();
      executeCommand();
    }
  };

  const updateMentionCommand = React.useCallback((nextValue, caret) => {
    if (!command.isSlashCommand || !command.isComplete || !selectedCommand) {
      setActiveMentionCommand(null);
      return;
    }

    const nextMentionCommand = resolveActiveCommand({
      caretPosition: caret,
      triggerSymbols: ['#', '$'],
      value: nextValue,
    });

    setActiveMentionCommand(nextMentionCommand);
    setIsMenuOpen(Boolean(nextMentionCommand));
  }, [command.isComplete, command.isSlashCommand, resolveActiveCommand, selectedCommand]);

  const handleCursorEvent = React.useCallback((event) => {
    updateMentionCommand(event.target.value, event.target.selectionStart);
  }, [updateMentionCommand]);

  React.useEffect(() => {
    if (!command.isSlashCommand || command.isComplete) {
      if (!activeMentionCommand) {
        setIsMenuOpen(false);
      }
      return;
    }

    setIsMenuOpen(true);
    setHighlightedIndex(0);
  }, [activeMentionCommand, command.commandName, command.isComplete, command.isSlashCommand]);

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [activeMentionCommand?.query, activeMentionCommand?.trigger]);

  React.useEffect(() => {
    if (!selectedCommand) {
      setActiveMentionCommand(null);
      onMentionedResourcesChange(EMPTY_ARRAY);
      return;
    }

    const mentionedResources = _.uniqBy(
        extractRecognizedMentions(value)
            .map((mention) => mention.definition?.connection)
            .filter(Boolean),
        (resource) => resource.rn,
    );
    onMentionedResourcesChange(mentionedResources);
  }, [extractRecognizedMentions, onMentionedResourcesChange, selectedCommand, value]);

  React.useEffect(() => {
    if (!activeMentionCommand || !selectedCommand) {
      return;
    }

    const searchQuery = activeMentionCommand.query || '';
    const isFirstLoad = searchQuery.length === 0;
    let mounted = true;
    setIsMentionCommandLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        if (activeMentionCommand.trigger === '#') {
          const filter = [];

          if (searchQuery) {
            filter.push({key: 'caregiverName', value: `${searchQuery}*`});
          }

          const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
          const result = await backend.caregiver.queryCaregiver(filter, sortBy);

          if (mounted) {
            setAvailableCaregivers((previousCaregivers) => _.uniqBy(
                [...(result?.list || []), ...previousCaregivers],
                (caregiver) => caregiver?.caregiverRn || caregiver?.connectionRn,
            ));
          }
        }

        if (activeMentionCommand.trigger === '$') {
          const filter = [
            {
              key: 'status',
              value: [
                'ACTIVE',
                'LEAD',
                'DETAILS_NEEDED',
                'CG_SEARCH',
                'CG_SEARCH_ACTIVE',
                'CG_PROFILE_SENT',
                'MEET_GREET',
                'STALLED',
              ],
            },
          ];

          if (searchQuery) {
            filter.push({key: 'clientName', value: `${searchQuery}*`});
          }

          const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
          const result = await backend.client.queryClient(filter, sortBy);

          if (mounted) {
            setAvailableClients((previousClients) => _.uniqBy(
                [...(result?.list || []), ...previousClients],
                (client) => client?.clientRn || client?.connectionRn,
            ));
          }
        }
      } catch (error) {
        console.error('Failed to load command mention resources', error);
      } finally {
        if (mounted) {
          setIsMentionCommandLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [activeMentionCommand, selectedCommand]);

  return (
    <Box
      sx={{
        p: '12px',
      }}
    >
      <Box ref={anchorRef} sx={{position: 'relative'}}>
        {
          showHighlightedOverlay &&
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {renderHighlightedText(value)}
          </Box>
        }
        <TextField
          value={value}
          onChange={handleChange}
          fullWidth
          autoFocus={autoFocus}
          placeholder={placeholder || translate('skills.SkillsModule.Textbox.Placeholder')}
          autoComplete='off'
          disabled={isExecutingCommand}
          inputRef={inputRef}
          InputProps={{
            endAdornment: isExecutingCommand ? (
              <InputAdornment position='end'>
                <CircularProgress color='inherit' size={20} />
              </InputAdornment>
            ) : null,
          }}
          onFocus={() => {
            if ((command.isSlashCommand && !command.isComplete) || activeMentionCommand) {
              setIsMenuOpen(true);
            }
          }}
          onKeyUp={handleCursorEvent}
          onKeyDown={handleKeyDown}
          onClick={handleCursorEvent}
          sx={{
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'common.white',
              'borderRadius': '4px',
              '& fieldset': {
                'borderStyle': 'dashed',
                'borderColor': '#A8A49E',
              },
            },
            '& .MuiOutlinedInput-input': {
              'position': 'relative',
              'zIndex': 2,
              'px': '16px',
              'py': '10px',
              'fontSize': '11px',
              'lineHeight': '150%',
              'whiteSpace': 'pre',
              'color': showHighlightedOverlay ? 'transparent' : '#1A1916',
              'WebkitTextFillColor': showHighlightedOverlay ? 'transparent' : 'unset',
              'caretColor': '#1A1916',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              'color': '#A8A49E',
              'opacity': 1,
            },
          }}
        />
      </Box>
      <Popper
        open={showMenu}
        anchorEl={anchorRef.current}
        onClickAway={() => setIsMenuOpen(false)}
        placement='bottom-start'
        zIndex={1400}
        GrowSx={{
          width: menuWidth,
        }}
      >
        <Stack
          sx={{
            width: menuWidth,
            maxHeight: '280px',
            overflowY: 'auto',
            py: '6px',
          }}
        >
          {
            showSlashMenu && filteredCommands.length === 0 &&
            <Typography
              sx={{
                px: '16px',
                py: '12px',
                fontSize: '12px',
                fontWeight: 400,
                color: '#A8A49E',
                lineHeight: '150%',
              }}
            >
              <Translate expression='skills.SkillsModule.Textbox.NoMatch' />
            </Typography>
          }
          {
            showMentionMenu && isMentionCommandLoading && mentionOptions.length === 0 &&
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: '12px',
              }}
            >
              <CircularProgress size={18} />
            </Box>
          }
          {
            showMentionMenu && !isMentionCommandLoading && mentionOptions.length === 0 &&
            <Typography
              sx={{
                px: '16px',
                py: '12px',
                fontSize: '12px',
                fontWeight: 400,
                color: '#A8A49E',
                lineHeight: '150%',
              }}
            >
              <Translate expression='skills.SkillsModule.Textbox.NoMatch' />
            </Typography>
          }
          {
            showSlashMenu && filteredCommands.map((commandOption, index) => {
              const isHighlighted = index === highlightedIndex;

              return (
                <ButtonBase
                  key={commandOption.key}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCommand(commandOption)}
                  sx={{
                    'width': '100%',
                    'justifyContent': 'flex-start',
                    'textAlign': 'left',
                    'px': '14px',
                    'py': '10px',
                    'backgroundColor': isHighlighted ? '#F8F6F3' : '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#F8F6F3',
                    },
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{
                      width: '100%',
                      gap: '16px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#1A1916',
                        lineHeight: '100%',
                      }}
                    >
                      /{commandOption.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: '#A8A49E',
                        lineHeight: '100%',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {commandOption.type}
                    </Typography>
                  </Stack>
                </ButtonBase>
              );
            })
          }
          {
            showMentionMenu && mentionOptions.map((definition, index) => {
              const isHighlighted = index === highlightedIndex;

              return (
                <ButtonBase
                  key={`${definition.connection?.rn}-${definition.display}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyMentionOption(definition)}
                  sx={{
                    'width': '100%',
                    'justifyContent': 'flex-start',
                    'textAlign': 'left',
                    'px': '14px',
                    'py': '10px',
                    'backgroundColor': isHighlighted ? '#F8F6F3' : '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#F8F6F3',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: commandColors[activeMentionCommand?.trigger],
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '100%',
                    }}
                  >
                    {`${activeMentionCommand?.trigger || ''}${definition.display}`}
                  </Typography>
                </ButtonBase>
              );
            })
          }
        </Stack>
      </Popper>
    </Box>
  );
};

SlashCommandTextbox.propTypes = {
  autoFocus: PropTypes.bool,
  caregivers: PropTypes.array,
  clients: PropTypes.array,
  isExecutingCommand: PropTypes.bool.isRequired,
  onMentionedResourcesChange: PropTypes.func.isRequired,
  onExecutePlaybook: PropTypes.func,
  onExecuteSkill: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  playbooks: PropTypes.array,
  skills: PropTypes.array.isRequired,
};

export default React.memo(SlashCommandTextbox);
